import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchDataFromApi } from '../../helpers/api';
import { getCurrentToken } from '../../utility/getCurrentToken';
import axiosInstance from '../../utility/axiosInstance';
import {handleAxiosError} from "../../helpers/handleAxiosError";
import {Instance} from "./instancesSlice";

interface Volume {
    id: string;
    name: string;
    size: number;
    status: string;
    description?: string;
    created_at: string;
    [key: string]: any;
}

interface VolumesState {
    data: Volume[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: VolumesState = {
    data: [],
    status: 'idle',
    error: null,
};

// Cinder can say `available` while the surrounding redu workflow is still finishing. The workflow
// stores that durable phase in volume metadata so a freshly loaded list keeps the same operation
// state that a live socket update showed before refresh.
const normalizeVolumeOperation = (volume: Volume): Volume => {
    const phase = volume.metadata?.redu_provisioning_phase;
    if (phase === 'CREATING_VOLUME') return {...volume, status: 'creating'};
    if (phase === 'ATTACHING_VOLUME') return {...volume, status: 'attaching'};
    if (phase === 'DELETING_VOLUME') return {...volume, status: 'deleting'};
    return volume;
};

export const fetchVolumes = createAsyncThunk(
    'volumes/fetchVolumes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/v1/volumes/list');
            return response.data;
        } catch (error) {
            console.error("Failed to fetch DNS:", error);
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const volumesSlice = createSlice({
    name: 'volumes',
    initialState,
    reducers: {
        setVolumes: (state, action: PayloadAction<Volume[]>) => {
            state.data = action.payload.map(normalizeVolumeOperation);
        },
        resetVolumes: () => initialState,
        updateVolume: (state, action: PayloadAction<Volume>) => {
            const index = state.data.findIndex(
                (instance) => instance.id === action.payload.id
            );

            if (index !== -1) {
                state.data[index] = normalizeVolumeOperation(action.payload);
            } else {
                state.data.push(normalizeVolumeOperation(action.payload));
            }
        },
        removeVolume: (state, action: PayloadAction<{ id: string }>) => {
            state.data = state.data.filter(volume => volume.id !== action.payload.id);
        },
        setVolumeStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
            const volume = state.data.find(s => s.id === action.payload.id);
            if (volume) {
                volume.status = action.payload.status;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVolumes.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchVolumes.fulfilled, (state, action: PayloadAction<Volume[]>) => {
                state.status = 'succeeded';
                state.data = action.payload.map(normalizeVolumeOperation);
            })
            .addCase(fetchVolumes.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { setVolumes, resetVolumes, updateVolume, removeVolume, setVolumeStatus } = volumesSlice.actions;
export default volumesSlice.reducer;
